import {
  Button,
  ButtonVariants,
  Colors,
  Loader,
  TextBold,
  TextReg,
  useCheckoutModalStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useCheckoutCompletion } from '../hooks/useCheckoutCompletion';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
export function SummaryStep() {
  const styles = useDynamicStyles(createStyles);
  const { resetForm, setClientSecret, attendee } = useCheckoutFormStore();
  const {
    closeCheckoutModal,
    onSuccess,
    checkoutId,
    setCurrentStep,
    setCheckoutId,
  } = useCheckoutModalStore();
  const [completionState, setCompletionState] = useState<
    'polling' | 'success' | 'error'
  >('polling');
  const [errorMessage, setErrorMessage] = useState<string>();

  const handleComplete = () => {
    setCompletionState('success');
  };

  const handleError = (error: string) => {
    setErrorMessage(error);
    setCompletionState('error');
  };

  // Handle both paid tickets (polling) and free tickets (immediate completion)
  useCheckoutCompletion({
    checkoutId,
    onComplete: handleComplete,
    onError: handleError,
  });

  const handleCloseAndReset = () => {
    closeCheckoutModal();
    resetForm();
    onSuccess?.();
  };

  const handleRetry = () => {
    setClientSecret(undefined);
    setCheckoutId(undefined);
    setCurrentStep('attendeeDetails');
  };

  if (completionState === 'polling') {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <Loader style={{ flex: 0, marginBottom: 16 }} />
          <TextBold style={styles.title}>Processing your order...</TextBold>
          <TextReg style={styles.subtitle}>
            Please do not close this page
          </TextReg>
        </View>
      </View>
    );
  }

  if (completionState === 'error') {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <TextBold style={styles.errorTitle}>Payment Failed</TextBold>
          <TextReg style={styles.errorMessage}>{errorMessage}</TextReg>
        </View>
        <View style={styles.footer}>
          <Button
            variant={ButtonVariants.WHITE}
            onPress={handleCloseAndReset}
            style={styles.button}
          >
            Close
          </Button>
          {Boolean(attendee) && (
            <Button
              variant={ButtonVariants.PURPLE}
              onPress={handleRetry}
              style={styles.button}
            >
              Back to Details
            </Button>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <TextBold style={styles.successTitle}>Success</TextBold>
        <TextBold style={styles.emoji}>🎉</TextBold>
        <TextReg style={styles.successMessage}>
          Check your profile for your ticket!
        </TextReg>
      </View>
      <View style={styles.footer}>
        <Button
          variant={ButtonVariants.PURPLE}
          onPress={handleCloseAndReset}
          style={styles.button}
        >
          Done
        </Button>
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    content: {
      flex: 1,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
      gap: 16,
    },
    title: {
      fontSize: 24,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: 16,
      textAlign: 'center',
    },
    emoji: {
      fontSize: 64,
    },
    errorTitle: {
      fontSize: 32,
      color: Colors.error,
      textAlign: 'center',
    },
    errorMessage: {
      fontSize: 16,
      textAlign: 'center',
    },
    successTitle: {
      fontSize: 32,
      textAlign: 'center',
    },
    successMessage: {
      fontSize: 16,
      textAlign: 'center',
    },
    footer: {
      padding: isMobile ? 12 : 16,
      paddingBottom: isMobile ? 24 : 12,
      gap: 12,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    button: {
      minWidth: 170,
    },
  });
